import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import { fetchNotifications, markNotificationsRead, fileUrl, trackAdClick } from "@/lib/api";
import { useTranslation } from "react-i18next";
import { Bell, Megaphone, MapPin, ExternalLink, Loader2, Paperclip } from "lucide-react";
import AdStatusAlerts from "@/components/AdStatusAlerts";
import { AdSeen } from "@/lib/useAdView";

/**
 * Customer-facing locality notifications. Reuses the existing PIN state —
 * active, admin-approved, paid, in-window ads whose target PIN matches the
 * shopper's selected PIN are surfaced here.
 */
export default function NotificationsPage() {
  const { user, pincode, authChecking, refreshUnread } = useApp();
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [unreadIds, setUnreadIds] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [alertsKey, setAlertsKey] = useState(0);

  const load = async () => {
    setLoading(true);
    try {
      const list = (await fetchNotifications(pincode)) || [];
      setItems(list);
      const fresh = list.filter((n) => !n.read).map((n) => n.id);
      setUnreadIds(new Set(fresh));
      if (fresh.length) await markNotificationsRead(fresh).catch(() => {});
      refreshUnread();
    } catch { setItems([]); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    if (authChecking) return;
    load();
    // eslint-disable-next-line
  }, [user?.id, pincode, authChecking]);

  if (!user && !authChecking) {
    return (
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10" data-testid="notifications-page">
        <div className="bg-amber-50 border border-amber-100 rounded-2xl p-6 text-center">
          <Bell className="w-10 h-10 text-amber-600 mx-auto mb-2" />
          <div className="font-display text-xl font-bold">Login to see notifications</div>
          <p className="text-sm text-slate-500 mt-1">Local sponsored shops and offers appear here based on your PIN.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6 sm:py-10" data-testid="notifications-page">
      <div className="flex items-center justify-between gap-2 flex-wrap mb-4">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold flex items-center gap-2">
            <Bell className="w-6 h-6 text-emerald-700" /> {t("nav.notifications")}
          </h1>
          <p className="text-sm text-slate-500 mt-0.5">Sponsored by local shops near PIN <b>{pincode || "—"}</b>.</p>
        </div>
        <button onClick={() => { load(); setAlertsKey((k) => k + 1); }} className="text-xs font-semibold text-emerald-700 border border-emerald-200 hover:bg-emerald-50 rounded-full px-3 py-1.5" data-testid="notifications-refresh">
          Refresh
        </button>
      </div>

      <AdStatusAlerts refreshKey={alertsKey} />

      {loading ? (
        <div className="py-10 text-center text-slate-500 text-sm inline-flex items-center justify-center gap-2 w-full">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading...
        </div>
      ) : items.length === 0 ? (
        <div className="bg-white border border-slate-100 rounded-2xl p-8 text-center" data-testid="notifications-empty">
          <Megaphone className="w-10 h-10 text-slate-300 mx-auto mb-2" />
          <div className="font-display text-lg font-bold">No local offers right now</div>
          <p className="text-sm text-slate-500 mt-1">We'll let you know when a nearby shop sponsors a product or service in <b>{pincode || "your PIN"}</b>.</p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="notifications-list">
          {items.map((n) => {
            const p = n.preview || {};
            return (
              <AdSeen key={n.id} adId={n.id} pincode={n.pincode} placement="notifications">
              <Link
                to={`/ad/${n.id}`}
                onClick={() => trackAdClick(n.id, n.pincode)}
                className="block bg-white border border-slate-100 hover:border-emerald-200 hover:shadow-sm rounded-2xl p-3 sm:p-4 transition-all"
                data-testid={`notification-${n.id}`}
              >
                <div className="flex items-start gap-3">
                  {p.image ? <img src={p.image} alt="" className="w-16 h-16 sm:w-20 sm:h-20 rounded-xl object-cover bg-slate-100 flex-shrink-0" /> : <span className="w-16 h-16 sm:w-20 sm:h-20 rounded-xl bg-amber-50 text-amber-600 flex items-center justify-center flex-shrink-0"><Megaphone className="w-6 h-6" /></span>}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2 flex-wrap">
                      <div className="min-w-0">
                        <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 border border-amber-100 text-amber-800 text-[10px] uppercase font-bold tracking-wider px-1.5 py-0.5">
                          <Megaphone className="w-3 h-3" /> {t("card.sponsored")}
                        </span>
                        {unreadIds.has(n.id) && <span className="ms-1.5 inline-block w-2 h-2 rounded-full bg-rose-600 align-middle" data-testid={`notification-unread-${n.id}`} />}
                        {n.title && <div className="text-sm font-bold text-slate-900 mt-1 line-clamp-1" data-testid={`notification-title-${n.id}`}>{n.title}</div>}
                        {n.message && <div className="text-xs text-slate-600 line-clamp-2">{n.message}</div>}
                        <div className="text-sm font-semibold text-slate-900 mt-1 line-clamp-1">{p.name}</div>
                        {p.shop_name && <div className="text-[11px] text-slate-500 line-clamp-1">by {p.shop_name}</div>}
                        {p.tagline && <div className="text-[11px] text-slate-500 line-clamp-1">{p.tagline}</div>}
                      </div>
                      <div className="text-[11px] text-slate-400 inline-flex items-center gap-1 flex-shrink-0"><MapPin className="w-3 h-3" /> {n.pincode}</div>
                    </div>
                    <div className="mt-2 flex items-center justify-between gap-2 flex-wrap">
                      {typeof p.price === "number" && <div className="text-sm font-bold text-emerald-800">₹{p.price}</div>}
                      <span className="inline-flex items-center gap-1 text-xs text-emerald-700 font-semibold">
                        Open <ExternalLink className="w-3 h-3" />
                      </span>
                    </div>
                    {(n.attachments || []).length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-1.5">
                        {n.attachments.map((a) => (
                          <button type="button" key={a.file_id} onClick={(e) => { e.preventDefault(); e.stopPropagation(); window.open(fileUrl(a.url), "_blank", "noopener"); }} className="inline-flex items-center gap-1 max-w-[12rem] rounded-full border border-slate-200 px-2 py-0.5 text-[11px] text-slate-700 hover:border-emerald-400" data-testid={`notification-attachment-${a.file_id}`}>
                            <Paperclip className="w-3 h-3 flex-shrink-0" /><span className="truncate">{a.filename}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </Link>
              </AdSeen>
            );
          })}
        </div>
      )}
    </div>
  );
}
